import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  TableCell,
  TableRow,
  Chip,
  IconButton,
  TextField,
  InputAdornment,
} from '@mui/material';
import { Visibility, Search as SearchIcon } from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import AccountantLayout from '../components/AccountantLayout';
import PaginatedTable from '../components/PaginatedTable';

const IncomingInvoicesPage = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [invoices, setInvoices] = useState([]);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [totalCount, setTotalCount] = useState(0);
  const [searchQuery, setSearchQuery] = useState('');

  useEffect(() => {
    loadInvoices();
  }, [page, rowsPerPage, searchQuery]);

  const loadInvoices = async () => {
    setLoading(true);
    // Simulate API call
    setTimeout(() => {
      const mockInvoices = [
        { id: 1, invoiceNumber: 'INV-2026-001', client: 'Atlas Construction Co', date: '2026-06-15', amount: 12500, status: 'Paid' },
        { id: 2, invoiceNumber: 'INV-2026-002', client: 'Meridian Logistics', date: '2026-06-18', amount: 8750, status: 'Pending' },
        { id: 3, invoiceNumber: 'INV-2026-003', client: 'Orchard Foods Ltd', date: '2026-06-20', amount: 15200, status: 'Overdue' },
        { id: 4, invoiceNumber: 'INV-2026-004', client: 'Seabreeze Properties', date: '2026-06-22', amount: 9800, status: 'Paid' },
        { id: 5, invoiceNumber: 'INV-2026-005', client: 'Helix Software BV', date: '2026-06-25', amount: 11200, status: 'Pending' },
      ];
      setInvoices(mockInvoices);
      setTotalCount(mockInvoices.length);
      setLoading(false);
    }, 500);
  };

  const columns = [
    { id: 'invoiceNumber', label: t('invoice_number'), minWidth: 150 },
    { id: 'client', label: t('client'), minWidth: 200 },
    { id: 'date', label: t('date'), minWidth: 120 },
    { id: 'amount', label: t('amount'), minWidth: 120, align: 'right' },
    { id: 'status', label: t('status'), minWidth: 100 },
    { id: 'actions', label: t('actions'), minWidth: 80, align: 'center' },
  ];

  const getStatusColor = (status) => {
    switch (status) {
      case 'Paid':
        return { bg: '#E8F5E9', color: '#4CAF50' };
      case 'Pending':
        return { bg: '#FFF3E0', color: '#FF9800' };
      case 'Overdue':
        return { bg: '#FFEBEE', color: '#F44336' };
      default:
        return { bg: '#F5F5F5', color: '#666' };
    }
  };

  const renderRow = (invoice) => {
    const statusColors = getStatusColor(invoice.status);
    return (
      <TableRow hover key={invoice.id}>
        <TableCell sx={{ fontWeight: 500 }}>{invoice.invoiceNumber}</TableCell>
        <TableCell>{invoice.client}</TableCell>
        <TableCell>{invoice.date}</TableCell>
        <TableCell align="right">€{invoice.amount.toLocaleString()}</TableCell>
        <TableCell>
          <Chip
            label={invoice.status}
            size="small"
            sx={{
              backgroundColor: statusColors.bg,
              color: statusColors.color,
              fontWeight: 500,
            }}
          />
        </TableCell>
        <TableCell align="center">
          <IconButton size="small" color="primary">
            <Visibility fontSize="small" />
          </IconButton>
        </TableCell>
      </TableRow>
    );
  };

  return (
    <AccountantLayout>
      <Box>
        <Box sx={{ mb: 3 }}>
          <Typography variant="h4" sx={{ fontWeight: 'bold', mb: 0.5 }}>
            {t('incoming_invoices')}
          </Typography>
          <Typography variant="body2" color="textSecondary">
            {t('manage_incoming_invoices')}
          </Typography>
        </Box>

        <Box sx={{ mb: 3 }}>
          <TextField
            fullWidth
            placeholder={t('search_invoices_placeholder')}
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon />
                </InputAdornment>
              ),
            }}
            sx={{
              backgroundColor: 'white',
              borderRadius: '8px',
              '& .MuiOutlinedInput-root': {
                borderRadius: '8px',
              },
            }}
          />
        </Box>

        <PaginatedTable
          columns={columns}
          data={invoices}
          loading={loading}
          page={page}
          rowsPerPage={rowsPerPage}
          totalCount={totalCount}
          onPageChange={setPage}
          onRowsPerPageChange={setRowsPerPage}
          emptyMessage={t('no_invoices_found')}
          renderRow={renderRow}
        />
      </Box>
    </AccountantLayout>
  );
};

export default IncomingInvoicesPage;
